import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';

const ConferenceSpinwheel = () => {
  const { conferenceId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  const [conference, setConference] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const fetchConferenceData = async () => {
      try {
        // Fetch conference data
        const conferenceRef = doc(db, 'conferences', conferenceId);
        const conferenceSnap = await getDoc(conferenceRef);
        
        if (!conferenceSnap.exists()) {
          console.error('Conference not found');
          setLoading(false);
          return;
        }

        const conferenceData = {
          id: conferenceSnap.id,
          ...conferenceSnap.data(),
          startDate: conferenceSnap.data().startDate?.toDate() || null,
          endDate: conferenceSnap.data().endDate?.toDate() || null,
          date: conferenceSnap.data().date?.toDate() || conferenceSnap.data().startDate?.toDate() || null
        };
        setConference(conferenceData);

        // Fetch leads from subcollection
        const leadsRef = collection(db, 'conferences', conferenceId, 'leads');
        const leadsQuery = query(leadsRef, orderBy('date', 'desc'));
        const leadsSnapshot = await getDocs(leadsQuery);
        
        const leadsData = leadsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || doc.data().createdAt?.toDate() || null
        }));
        
        setLeads(leadsData);
      } catch (error) {
        console.error('Error fetching conference data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (conferenceId) {
      fetchConferenceData();
    }
  }, [conferenceId]);

  const spinWheel = () => {
    if (leads.length === 0 || isSpinning) return;

    setIsSpinning(true);
    setSelectedLead(null);

    // Random rotation (multiple full spins + random angle)
    const baseRotations = 5; // Number of full rotations
    const randomAngle = Math.random() * 360;
    const totalRotation = baseRotations * 360 + randomAngle;
    const newRotation = rotation + totalRotation;

    setRotation(newRotation);

    // Calculate which lead is selected based on final rotation
    setTimeout(() => {
      const normalizedAngle = (360 - (newRotation % 360)) % 360;
      const segmentAngle = 360 / leads.length;
      const selectedIndex = Math.floor(normalizedAngle / segmentAngle);
      const finalIndex = selectedIndex >= leads.length ? 0 : selectedIndex;
      
      setSelectedLead(leads[finalIndex]);
      setIsSpinning(false);
    }, 5000); // Match animation duration
  };

  if (loading) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <BackButton />
          <div className="text-center mt-8">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading conference...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!conference) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <BackButton />
          <div className="text-center mt-8">
            <p className="text-gray-600 dark:text-gray-400">Conference not found</p>
          </div>
        </div>
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <BackButton />
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">
              {conference.name} - Spin Wheel
            </h1>
            <div className="w-24"></div>
          </div>
          <div className="text-center mt-16">
            <p className="text-gray-600 dark:text-gray-400 text-lg">No leads available to spin</p>
          </div>
        </div>
      </div>
    );
  }

  const segmentAngle = 360 / leads.length;
  const colors = [
    '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', 
    '#EC4899', '#06B6D4', '#F97316', '#84CC16', '#6366F1'
  ];

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <BackButton />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">
            {conference.name} - Spin Wheel
          </h1>
          <div className="w-24"></div>
        </div>

        <div className="flex flex-col items-center justify-center mt-12">
          {/* Wheel Container */}
          <div className="relative mb-12">
            <div className="relative w-96 h-96">
              {/* Pointer */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 z-10">
                <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] border-t-red-600"></div>
              </div>

              {/* Wheel */}
              <svg
                width="384"
                height="384"
                viewBox="0 0 384 384"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
                }}
              >
                <defs>
                  {leads.map((lead, index) => (
                    <linearGradient key={index} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={colors[index % colors.length]} stopOpacity="0.9" />
                      <stop offset="100%" stopColor={colors[index % colors.length]} stopOpacity="0.7" />
                    </linearGradient>
                  ))}
                </defs>
                {leads.map((lead, index) => {
                  const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
                  const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
                  const largeArcFlag = segmentAngle > 180 ? 1 : 0;
                  
                  const x1 = 192 + 192 * Math.cos(startAngle);
                  const y1 = 192 + 192 * Math.sin(startAngle);
                  const x2 = 192 + 192 * Math.cos(endAngle);
                  const y2 = 192 + 192 * Math.sin(endAngle);

                  const midAngle = (startAngle + endAngle) / 2;
                  const textX = 192 + 140 * Math.cos(midAngle);
                  const textY = 192 + 140 * Math.sin(midAngle);

                  return (
                    <g key={index}>
                      <path
                        d={`M 192 192 L ${x1} ${y1} A 192 192 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                        fill={`url(#gradient-${index})`}
                        stroke="#fff"
                        strokeWidth="2"
                      />
                      <text
                        x={textX}
                        y={textY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize="14"
                        fontWeight="bold"
                        className="pointer-events-none"
                        transform={`rotate(${index * segmentAngle + segmentAngle / 2 - 90}, ${textX}, ${textY})`}
                      >
                        {lead.firstName || lead.first_name || 'Lead'} {lead.lastName || lead.last_name || index + 1}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Spin Button */}
          <button
            onClick={spinWheel}
            disabled={isSpinning || leads.length === 0}
            className={`px-8 py-4 rounded-lg text-white font-bold text-lg transition-all ${
              isSpinning || leads.length === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 active:scale-95'
            }`}
          >
            {isSpinning ? 'Spinning...' : 'Spin the Wheel!'}
          </button>

          {/* Selected Lead Display */}
          {selectedLead && (
            <div className="mt-12 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                🎉 Winner! 🎉
              </h2>
              <p className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-center">
                {selectedLead.firstName || selectedLead.first_name || ''} {selectedLead.lastName || selectedLead.last_name || ''}
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-400 text-center mt-2">
                {selectedLead.companyName || selectedLead.company || ''}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConferenceSpinwheel;

